import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, Home, HandCoins, PawPrint } from "lucide-react";
import useReveal from "../lib/useReveal";
import useSeo from "../lib/useSeo";
import useJsonLd from "../lib/useJsonLd";
import { SITE_URL, siteUrl } from "../lib/siteConfig";
import { CollaborateForm } from "./Landing";
import FCCSection from "../components/FCCSection";
import DonateSection from "../components/DonateSection";

const FOUNDATION_IMG = "/foundation-craft.jpg";
const ANIMAL_IMG = "/foundation-dog.jpg";

const PILLARS = [
  {
    icon: Home,
    kicker: "Pillar 01",
    title: "Homes & schools rebuilt",
    body: "We fund and closely supervise the rebuilding of homes and schools in unsafe, neglected and unhygienic conditions across India — creating spaces that are safe, dignified and built to last.",
  },
  {
    icon: HandCoins,
    kicker: "Pillar 02",
    title: "Financial assistance & livelihoods",
    body: "We help revive livelihoods across India by providing financial support, tools and guidance — enabling people to start or grow small businesses, secure a steady income and live with dignity. Where needed, we also support basic shelter — repairing or rebuilding homes to create a safer foundation for life.",
  },
  {
    icon: PawPrint,
    kicker: "Pillar 03",
    title: "Animal welfare",
    body: "We support rescue, rehabilitation and sterilisation drives across India — working with local shelters to give stray animals a real chance at care, safety and survival.",
  },
];

function Reveal({ children, className = "" }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`sba-reveal ${className}`}>
      {children}
    </div>
  );
}

export default function Foundation({ focus, mode, region }) {
  // Customise SEO when arrived via a shareable shortcut URL
  // (e.g. /donate, /join-the-circle, /donate-now, /fcc, /international-*).
  const seo = (() => {
    const intl = region === "international";
    if (focus === "donate" && mode === "monthly") {
      return {
        title: intl
          ? "Become a Monthly Patron — Stories by Aradhana"
          : "Join the Foundation for Change Circle — Stories by Aradhana",
        description: intl
          ? "International Patrons — become a monthly Patron supporting our storytelling and impact work. Service contribution via PayPal (USD/GBP/EUR). Not a charitable donation."
          : "Become a monthly donor with the Foundation for Change Circle. A small, close-knit community sustaining continued care for the people whose stories we've shared.",
      };
    }
    if (focus === "donate" && mode === "one-time") {
      return {
        title: intl
          ? "Support as an International Patron — Stories by Aradhana"
          : "Make a one-time donation — Stories by Aradhana Foundation",
        description: intl
          ? "International Patrons — one-time contribution supporting our storytelling work. Service purchase via PayPal (USD/GBP/EUR). Not a charitable donation eligible for Indian tax exemption."
          : "Make a one-time donation to the Stories by Aradhana Foundation for Change. Section 8 · Sections 332 & 354 (erstwhile 12A & 80G) registered.",
      };
    }
    if (focus === "donate") {
      return {
        title: "Donate to the Foundation — Stories by Aradhana",
        description:
          "Support the Stories by Aradhana Foundation for Change. Monthly or one-time donations. Section 8 · Sections 332 & 354 (erstwhile 12A & 80G) registered.",
      };
    }
    if (focus === "fcc") {
      return {
        title: "The Foundation for Change Circle — Stories by Aradhana",
        description:
          "FCC is a recurring support community sustaining continued care, follow-up and new stories. A minimum of 75% of every contribution goes directly to beneficiaries.",
      };
    }
    return {
      title: "Foundation for Change — Stories by Aradhana",
      description:
        "The Stories by Aradhana Foundation for Change builds homes and schools, restores livelihoods, and works on animal welfare across India. Where the story ends, our work begins.",
    };
  })();
  useSeo(seo);

  // Auto-scroll to the relevant section when arriving via a shareable URL.
  useEffect(() => {
    if (!focus) return;
    const targetId = focus === "fcc" ? "fcc" : "donate";
    const tryScroll = () => {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return true;
      }
      return false;
    };
    if (!tryScroll()) {
      setTimeout(tryScroll, 100);
      setTimeout(tryScroll, 300);
    }
  }, [focus]);

  useJsonLd(
    {
      "@context": "https://schema.org",
      "@type": "NGO",
      name: "Stories by Aradhana Foundation for Change",
      alternateName: "Foundation for Change",
      url: siteUrl("/foundation"),
      logo: siteUrl("/logo.png"),
      image: siteUrl("/foundation-craft.jpg"),
      description:
        "The on-ground extension of Stories by Aradhana — building homes and schools, providing financial assistance and restoring livelihoods, and working on animal welfare initiatives across India.",
      areaServed: { "@type": "Country", name: "India" },
      parentOrganization: {
        "@type": "Organization",
        name: "Stories by Aradhana",
        url: SITE_URL,
      },
    },
    "foundation-ngo"
  );
  return (
    <main data-testid="foundation-page" className="bg-[#fdfbf7]">
      {/* HERO */}
      <section className="bg-[#2d4a22] text-[#fdfbf7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-10 md:pt-24 pb-24 md:pb-32">
          <Reveal>
            <Link
              to="/"
              data-testid="foundation-back-home"
              className="sba-link-arrow font-mono text-[11px] tracking-[0.25em] uppercase text-[#fdfbf7]/70 hover:text-[#fdfbf7]"
            >
              <ArrowLeft size={14} strokeWidth={1.4} /> Back to Stories
            </Link>
          </Reveal>
          <div className="mt-8 lg:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 lg:gap-20 items-end">
            <div className="lg:col-span-7 order-2 lg:order-none">
              <Reveal>
                <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#fdfbf7]/80">
                  Stories by Aradhana Foundation for Change
                </p>
              </Reveal>
              <Reveal className="mt-6">
                <h1 className="font-serif text-[clamp(2.4rem,5.6vw,4.75rem)] leading-[1] tracking-tight">
                  <span className="whitespace-nowrap">
                    Where the story <em className="italic">ends</em>,
                  </span>
                  <br />
                  our work&nbsp;
                  <span className="underline decoration-[#f9d94b] decoration-[6px] underline-offset-[10px]">
                    begins
                  </span>
                  .
                </h1>
              </Reveal>
              <Reveal className="mt-10 max-w-xl">
                <p className="font-sans text-[17px] md:text-[18px] leading-relaxed text-[#fdfbf7]/80">
                  The on-ground extension of Stories by Aradhana — building
                  homes and schools, providing financial assistance and
                  restoring livelihoods, and working on animal welfare
                  initiatives across India.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-none">
              <Reveal>
                <div className="relative group mt-2 lg:mt-0">
                  <div className="absolute -top-3 -left-3 lg:-top-5 lg:-left-5 w-14 h-14 lg:w-28 lg:h-28 bg-[#f9d94b] -z-0 transition-transform duration-700 ease-out group-hover:-translate-x-1 group-hover:-translate-y-1" />
                  <div className="relative z-10 w-full aspect-[4/3] overflow-hidden">
                    <img
                      src={FOUNDATION_IMG}
                      alt="Artisans at work — Stories by Aradhana Foundation for Change"
                      data-testid="foundation-hero-image"
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                  <p className="relative z-10 mt-4 font-mono text-[11px] tracking-[0.25em] uppercase text-[#f9d94b]">
                    Foundation for Change — On-ground, West Bengal
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d4a22]">
            What we do
          </p>
          <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#1a1a1a] max-w-2xl">
            Three pillars. One promise — stay till the story is finished.
          </h2>
        </Reveal>

        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
          {PILLARS.map(({ icon: Icon, kicker, title, body }, i) => (
            <Reveal key={title}>
              <div
                data-testid={`pillar-${i + 1}`}
                className="border-t border-[#2d4a22]/30 pt-8"
              >
                <Icon
                  size={28}
                  strokeWidth={1.2}
                  className="text-[#2d4a22]"
                />
                <p className="mt-8 font-mono text-[11px] tracking-[0.2em] uppercase text-[#4a4a4a]">
                  {kicker}
                </p>
                <h3 className="mt-3 font-serif text-3xl leading-tight tracking-tight text-[#1a1a1a]">
                  {title}
                </h3>
                <p className="mt-5 font-sans text-[15px] leading-relaxed text-[#4a4a4a]">
                  {body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ON THE GROUND — trust bridge before FCC */}
      <section className="bg-[#1a1a1a] text-[#fdfbf7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-5">
            <img
              src={ANIMAL_IMG}
              alt="A rescued animal finds a home"
              loading="lazy"
              decoding="async"
              className="w-full h-[380px] md:h-[480px] object-cover"
            />
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7">
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#f9d94b]">
              On the ground
            </p>
            <h3 className="mt-6 font-serif text-3xl md:text-4xl leading-[1.1] tracking-tight">
              From a viewer’s heart to a family’s doorstep.
            </h3>
            <p className="mt-6 font-sans text-[17px] md:text-[18px] leading-relaxed text-[#fdfbf7]/75 max-w-xl">
              Every campaign we run is traceable — the homes rebuilt, the
              livelihoods restored, the lives changed. We show exactly where
              the money goes, because trust is the only currency worth
              growing.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FCC NARRATIVE */}
      <FCCSection />

      {/* DONATE SECTION */}
      <DonateSection defaultMode={mode || "monthly"} defaultRegion={region || "india"} />

      {/* CSR / BRAND PARTNERSHIPS */}
      <section
        id="foundation-csr"
        data-testid="foundation-csr"
        className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16"
      >
        <Reveal className="lg:col-span-5">
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d4a22]">
            For CSR &amp; brand partnerships
          </p>
          <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#1a1a1a]">
            Partner with the
            <br />
            <em className="italic">Foundation</em>.
          </h2>
          <p className="mt-6 font-sans text-[17px] leading-relaxed text-[#4a4a4a] max-w-sm">
            For corporates and CSR teams looking to fund a story end-to-end,
            sponsor a campaign for a named beneficiary, or back our on-ground
            work at scale — write to the Foundation directly. We&rsquo;ll come
            back with a proposal tailored to your CSR goals and reporting needs.
          </p>
        </Reveal>
        <div className="lg:col-span-7">
          <CollaborateForm kind="foundation" />
        </div>
      </section>
    </main>
  );
}
